// Optional per-card design overrides (custom accent colour, font).
// The preset colour from the organisation settings stays the default; when a
// card sets `theme.customAccent` a synthetic preset is derived from it so all
// existing theme lookups (gradient, buttons, links) keep working unchanged.

import type { AppSettings, ThemeColor } from './cardUtils.js';
import { darkenHex } from './themeUtils.js';

export const CUSTOM_COLOR_NAME = '__custom';
export const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const CARD_FONTS: Record<string, { label: string; stack: string }> = {
  default: { label: 'Standard', stack: '' },
  serif: { label: 'Serif', stack: 'Georgia, "Times New Roman", serif' },
  rounded: { label: 'Rund', stack: 'ui-rounded, "SF Pro Rounded", "Nunito", system-ui, sans-serif' },
  mono: { label: 'Mono', stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
};

interface ThemeLike { color?: string | undefined; style?: string | undefined; customAccent?: string | undefined; font?: string | undefined }

export function withCustomAccent<T extends ThemeLike>(
  theme: T,
  settings: AppSettings | undefined
): { theme: T; settings: AppSettings | undefined } {
  const accent = theme.customAccent;
  if (!accent || !HEX_RE.test(accent)) return { theme, settings };
  const custom: ThemeColor = {
    name: CUSTOM_COLOR_NAME,
    gradientStyle: `linear-gradient(135deg, ${accent}, ${darkenHex(accent, 25)})`,
    buttonStyle: accent,
    linkStyle: accent,
    textStyle: accent,
  };
  const colors = (settings?.theme_colors ?? []).filter((c) => c.name !== CUSTOM_COLOR_NAME);
  return {
    theme: { ...theme, color: CUSTOM_COLOR_NAME },
    settings: { ...(settings ?? {}), theme_colors: [...colors, custom] } as AppSettings,
  };
}
